/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { keyExtractor } from '../../utils/PRIVATE_collectionUtils';
import { getRowIndexForRowElement, getElementFromCell } from './TableDomUtils';
import { TableColumn } from '../Table';
import { Cell, RowActionDetail, SortCriterionDetail } from '../../UNSAFE_TableView';

/**
 * Helper function to handle a row action gesture from a pointer or keyboard event
 */
export function handleRowActionGesture<K, D, C>(
  root: HTMLDivElement,
  tableId: string,
  data: D[],
  onRowAction: (detail: RowActionDetail<K, D>) => void,
  targetElement: HTMLElement,
  targetCell?: Cell<K, C>
) {
  let rowKey;
  let rowElement;
  if (targetCell?.type === 'data') {
    rowKey = targetCell.rowKey;
    const targetCellElement = getElementFromCell(root, tableId, targetCell);
    if (targetCellElement != null) {
      rowElement = targetCellElement.parentElement;
    }
  } else {
    rowKey = keyExtractor(targetElement, `[data-oj-table-data-row='${tableId}']`) as K;
    if (rowKey != null) {
      rowElement = targetElement.closest(`[data-oj-table-data-row='${tableId}']`);
    }
  }
  if (rowKey != null && rowElement != null) {
    const rowIndex = getRowIndexForRowElement(root, tableId, rowElement as HTMLElement);
    if (rowIndex != null) {
      const rowContext = {
        key: rowKey,
        data: data[rowIndex]
      };
      onRowAction({ context: rowContext, target: targetElement });
      return true;
    }
  }
  return false;
}

/**
 * Helper function to handle a row action gesture
 */
export function handleSortActionGesture<K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  columnKey: C,
  onSortCriterionChange: (detail: SortCriterionDetail<C>) => void,
  sortCriterion?: { key: C; sortDirection: 'ascending' | 'descending' }[]
) {
  for (const column of columnsArray) {
    if (column.key === columnKey) {
      if (column.value.sortable === 'enabled') {
        let sortDirection;
        if (sortCriterion != null) {
          for (const criterion of sortCriterion) {
            if (criterion.key === column.key) {
              sortDirection = criterion.sortDirection;
              break;
            }
          }
        }
        const newDirection = sortDirection === 'ascending' ? 'descending' : 'ascending';
        onSortCriterionChange([{ key: columnKey, sortDirection: newDirection }]);
        return true;
      }
      break;
    }
  }
  return false;
}
